import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import QRCode from 'react-qr-code';
import { Html5Qrcode } from 'html5-qrcode';
import {
  ShieldCheck, Broadcast, ArrowCounterClockwise, WarningCircle, HourglassMedium,
  CheckCircle, UsersFour, Bus, MapPin, Camera, PencilSimple, Keyboard, Prohibit, Siren,
  Car, LockKey, LockKeyOpen, Buildings,
} from '@phosphor-icons/react';
import Layout from '../../components/Layout';
import DialogModal from '../../components/DialogModal';
import LockedFeature from '../../components/LockedFeature';
import { useAuth } from '../../context/AuthContext';
import securityService from '../../services/securityService';
import transportService from '../../services/transportService';
import './security.css';

const QR_READER_ELEMENT_ID = 'sec-qr-reader';

const STATUS_META = {
  pending:             { label: 'Awaiting',    badge: 'badge-pending',    color: '#d97706' },
  en_route_bus:        { label: 'On Bus',       badge: 'badge-dispatched', color: '#16a34a' },
  en_route_collector:  { label: 'Collected',    badge: 'badge-verified',   color: '#16a34a' },
  arrived_unconfirmed: { label: 'Arrived',      badge: 'badge-qr_sent',    color: '#2563eb' },
  safe_confirmed:      { label: 'Home Safe',    badge: 'badge-verified',   color: '#16a34a' },
  qr_sent:             { label: 'QR Sent',      badge: 'badge-qr_sent',    color: '#2563eb' },
};

const initials = (name) =>
  name ? name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() : '?';

const formatTime = (d) =>
  d.toLocaleTimeString('en-GH', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });

const formatDate = (d) =>
  d.toLocaleDateString('en-GH', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

export default function SecurityDashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [now, setNow] = useState(new Date());
  const [closingTime, setClosingTime] = useState('15:00');
  const [students, setStudents] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [activeTab, setActiveTab] = useState('students');
  const [qrsScheduled, setQrsScheduled] = useState(false);
  const [qrsSent, setQrsSent] = useState(false);
  const [qrsDispatchedAt, setQrsDispatchedAt] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [pickupQueue, setPickupQueue] = useState({ on_the_way: [], waiting: [] });
  const [loadingQueue, setLoadingQueue] = useState(false);
  const [holdDialog, setHoldDialog] = useState(null); // { studentId, studentName, reason }
  const [savingHold, setSavingHold] = useState(false);
  const [locationDialog, setLocationDialog] = useState(null); // { studentId, studentName, location }
  const [savingLocation, setSavingLocation] = useState(false);

  // Scanner modal state
  const [scanModal, setScanModal] = useState(null);
  // scanModal shape: { student, step: 'enter_token'|'verifying'|'result', tokenInput, result, mode: 'camera'|'manual', cameraError }

  // Transport QR modal
  const [qrModal, setQrModal] = useState(null);

  // Pickup method edit modal (admin only)
  const [editModal, setEditModal] = useState(null);
  // editModal shape: { student, pickupMethod, transportRouteId, saving, error }
  const isAdmin = user?.role === 'super_admin' || user?.role === 'school_admin';

  // Live clock
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const addNotification = useCallback((text, color = 'blue') => {
    setNotifications(prev =>
      [{ id: Date.now(), text, color, time: formatTime(new Date()) }, ...prev].slice(0, 30)
    );
  }, []);

  // Load students and routes from API
  const loadData = useCallback(async () => {
    if (!user?.school_id) return;
    setLoading(true);
    setLoadError(null);
    try {
      const [studentsData, routesData] = await Promise.all([
        securityService.listStudents(user.school_id),
        transportService.listRoutes({ school_id: user.school_id }).catch(() => []),
      ]);
      const studentsArr = Array.isArray(studentsData) ? studentsData : [];
      setStudents(studentsArr);
      setRoutes(Array.isArray(routesData) ? routesData : []);
      // Determine whether QR codes have already been dispatched for today.
      // Prefer an explicit status endpoint if available, otherwise infer from student records.
      try {
        const data = await securityService.getQrStatus(user.school_id) || {};
        const sentToday = Boolean(data.sentToday || data.sent || data.dispatched || (data.count > 0));
        setQrsSent(sentToday);
        if (data.dispatched_at) setQrsDispatchedAt(data.dispatched_at);
        if (data.idempotency_key) {
          // store or surface if needed in future
        }
      } catch (_) {
        // Fallback: infer from student tokens or arrival statuses
        const inferred = studentsArr.some(s => s.arrival_status === 'qr_sent');
        setQrsSent(inferred);
      }
      addNotification(`System ready. Monitoring ${(studentsData || []).length} students.`, 'blue');
    } catch (err) {
      setLoadError('Failed to load student data. Check your connection.');
    } finally {
      setLoading(false);
    }
  }, [user?.school_id, addNotification]);

  useEffect(() => { loadData(); }, [loadData]);

  // Load the drive-through pickup queue (who's on their way / waiting)
  const loadPickupQueue = useCallback(async () => {
    if (!user?.school_id) return;
    setLoadingQueue(true);
    try {
      const data = await securityService.getPickupQueue(user.school_id);
      setPickupQueue({ on_the_way: data?.on_the_way || [], waiting: data?.waiting || [] });
    } catch (err) {
      // non-fatal — queue tab just stays empty
    } finally {
      setLoadingQueue(false);
    }
  }, [user?.school_id]);

  useEffect(() => { loadPickupQueue(); }, [loadPickupQueue]);

  // SSE subscription for multi-admin realtime updates
  useEffect(() => {
    if (!user?.school_id) return;
    let es;
    try {
      es = new EventSource(securityService.getEventsStreamUrl(user.school_id));
    } catch (err) {
      console.warn('SSE not available:', err);
      return;
    }

    es.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        if (data.type === 'qr_dispatch' && data.school_id === user.school_id) {
          setQrsSent(true);
          if (data.dispatched_at) setQrsDispatchedAt(data.dispatched_at);
          addNotification('QR dispatch recorded by another admin.', 'blue');
          // refresh counts
          loadData();
        }
        if (data.type === 'parent_on_the_way' && data.school_id === user.school_id) {
          addNotification('A parent signalled they are on their way to pick up a student.', 'blue');
          loadPickupQueue();
        }
        if (data.type === 'pickup_queue_updated' && data.school_id === user.school_id) {
          loadPickupQueue();
          loadData();
        }
        if (data.type === 'unauthorized_scan_attempt' && data.school_id === user.school_id) {
          addNotification(`Unauthorized pickup scan blocked: ${data.reason || 'unknown reason'}`, 'red');
        }
        if (data.type === 'custody_restriction_blocked' && data.school_id === user.school_id) {
          addNotification('A pickup-restricted parent attempted a pickup action. Review immediately.', 'red');
        }
        if (data.type === 'release_hold_set' && data.school_id === user.school_id) {
          addNotification(`Release hold placed: ${data.reason || 'no reason given'}`, 'red');
          loadData();
        }
        if (data.type === 'student_location_updated' && data.school_id === user.school_id) {
          loadData();
        }
      } catch (err) {
        console.warn('Failed parsing SSE event', err);
      }
    };

    es.onerror = (err) => {
      // EventSource will auto-reconnect — log for debugging
      console.warn('SSE error', err);
    };

    return () => {
      try { es.close(); } catch (_) {}
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.school_id]);

  // Auto-dispatch at closing time
  useEffect(() => {
    if (!qrsScheduled || qrsSent) return;
    const [closingH, closingM] = closingTime.split(':').map(Number);
    const closingMs = (closingH * 60 + closingM) * 60 * 1000;
    const nowMs = (now.getHours() * 60 + now.getMinutes()) * 60 * 1000 + now.getSeconds() * 1000;
    if (nowMs >= closingMs) handleDispatchQRs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [now, qrsScheduled, qrsSent, closingTime]);

  const getCountdown = () => {
    const [h, m] = closingTime.split(':').map(Number);
    const target = new Date(now);
    target.setHours(h, m, 0, 0);
    if (target <= now) target.setDate(target.getDate() + 1);
    const diff = Math.max(0, target - now);
    return {
      hh: Math.floor(diff / 3600000),
      mm: Math.floor((diff % 3600000) / 60000),
      ss: Math.floor((diff % 60000) / 1000),
    };
  };

  const handleDispatchQRs = async () => {
    if (!user?.school_id) {
      addNotification('QR dispatch failed: school is not available for this account.', 'red');
      return;
    }

    try {
      // Generate an idempotency key for safe retries / concurrent admins
      const idempotencyKey = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2,8)}`;
      const data = await securityService.generateDailyQr(user.school_id, idempotencyKey) || {};
      const success = Boolean(data.sent || data.created || (data.created === 0 && data.message));
      const dispatchedAt = data.dispatched_at || data.dispatchedAt || null;
      if (dispatchedAt) setQrsDispatchedAt(dispatchedAt);
      if (success) {
        setQrsSent(true);
        addNotification('QR codes generated and dispatched to all parent emails.', 'amber');
      } else {
        setQrsSent(true);
        addNotification('QR dispatch recorded (no new tokens were required).', 'amber');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || String(err);
      addNotification(`QR dispatch failed: ${msg}`, 'red');
    }
  };

  const handleDispatchTransport = async (route) => {
    try {
      await securityService.dispatchTransportRoute(route.id, user.school_id);
      addNotification(`${route.name} dispatched — live location sharing active`, 'green');
      loadData();
    } catch (err) {
      addNotification(`Dispatch failed: ${err.message}`, 'amber');
    }
  };

  // ── Pickup method editing (admin only) ──────────────────────────────────────

  const openEditPickup = (student) => {
    setEditModal({
      student,
      pickupMethod: student.pickup_method || 'parent',
      transportRouteId: student.transport_route_id || (routes[0]?.id ?? ''),
      saving: false,
      error: null,
    });
  };

  const handleSavePickup = async () => {
    if (!editModal) return;
    if (editModal.pickupMethod === 'transport' && !editModal.transportRouteId) {
      setEditModal(prev => ({ ...prev, error: 'Select a route first.' }));
      return;
    }
    setEditModal(prev => ({ ...prev, saving: true, error: null }));
    try {
      await securityService.updateSecurityProfile(editModal.student.id, {
        pickup_method: editModal.pickupMethod,
        transport_route_id: editModal.pickupMethod === 'transport' ? editModal.transportRouteId : null,
      });
      addNotification(`${editModal.student.name} set to ${editModal.pickupMethod === 'transport' ? 'school transport' : 'parent pickup'}`, 'blue');
      setEditModal(null);
      loadData();
    } catch (err) {
      setEditModal(prev => ({ ...prev, saving: false, error: err.message }));
    }
  };

  // ── Campus-exit hold ─────────────────────────────────────────────────────────

  const openHoldDialog = (student) => {
    setHoldDialog({ studentId: student.id, studentName: student.name, reason: '' });
  };

  const handleSetHold = async (reason) => {
    if (!holdDialog) return;
    setSavingHold(true);
    try {
      await securityService.setReleaseHold(holdDialog.studentId, reason);
      addNotification(`Release hold placed on ${holdDialog.studentName}.`, 'red');
      setHoldDialog(null);
      loadData();
    } catch (err) {
      addNotification(`Failed to set hold: ${err.message}`, 'red');
    } finally {
      setSavingHold(false);
    }
  };

  const handleClearHold = async (student) => {
    setSavingHold(true);
    try {
      await securityService.clearReleaseHold(student.id);
      addNotification(`Release hold lifted for ${student.name}.`, 'blue');
      loadData();
    } catch (err) {
      addNotification(`Failed to lift hold: ${err.message}`, 'red');
    } finally {
      setSavingHold(false);
    }
  };

  // ── On-campus location ───────────────────────────────────────────────────────

  const openLocationDialog = (student) => {
    setLocationDialog({ studentId: student.id, studentName: student.name, location: student.current_location || '' });
  };

  const handleSetLocation = async (location) => {
    if (!locationDialog || !location?.trim()) return;
    setSavingLocation(true);
    try {
      await securityService.updateStudentLocation(locationDialog.studentId, location.trim());
      addNotification(`${locationDialog.studentName} location set to "${location.trim()}".`, 'blue');
      setLocationDialog(null);
      loadData();
    } catch (err) {
      addNotification(`Failed to update location: ${err.message}`, 'red');
    } finally {
      setSavingLocation(false);
    }
  };

  // ── Scanner ───────────────────────────────────────────────────────────────

  const openScanner = (student) => {
    setScanModal({ student, step: 'enter_token', tokenInput: '', result: null, mode: 'camera', cameraError: null });
  };

  const handleVerifyToken = async (tokenOverride) => {
    const tokenValue = (tokenOverride ?? scanModal?.tokenInput ?? '').trim();
    if (!tokenValue || !scanModal) return;
    const student = scanModal.student;
    setScanModal(prev => (prev ? { ...prev, step: 'verifying', tokenInput: tokenValue } : prev));

    let gateLat = null;
    let gateLng = null;
    try {
      const pos = await new Promise((res, rej) =>
        navigator.geolocation.getCurrentPosition(res, rej, { timeout: 3000 })
      );
      gateLat = pos.coords.latitude;
      gateLng = pos.coords.longitude;
    } catch (_) { /* geolocation optional */ }

    try {
      const result = await securityService.verifyQr(tokenValue, gateLat, gateLng);
      setScanModal(prev => (prev ? { ...prev, step: 'result', result } : prev));

      if (result.result === 'authorized') {
        addNotification(
          `${student.name} authorized — collector tracking started`,
          'green'
        );
        loadData();
      } else {
        addNotification(
          `Unauthorized scan attempt for ${student.name} — ${result.reason || result.result}`,
          'amber'
        );
      }
    } catch (err) {
      setScanModal(prev => (prev ? {
        ...prev,
        step: 'result',
        result: { result: 'unauthorized', reason: err.message },
      } : prev));
    }
  };

  // Drive the camera scanner whenever the modal is in camera mode awaiting a scan
  useEffect(() => {
    if (!scanModal || scanModal.step !== 'enter_token' || scanModal.mode !== 'camera') {
      return;
    }

    let isActive = true;
    let started = false;
    const html5Qr = new Html5Qrcode(QR_READER_ELEMENT_ID);

    html5Qr.start(
      { facingMode: 'environment' },
      { fps: 10, qrbox: 220 },
      (decodedText) => {
        if (!isActive) return;
        isActive = false;
        if (started) html5Qr.stop().catch(() => {});
        handleVerifyToken(decodedText);
      },
      () => { /* per-frame scan miss — ignore */ }
    ).then(() => {
      started = true;
    }).catch((err) => {
      if (!isActive) return;
      setScanModal(prev => (prev ? {
        ...prev,
        mode: 'manual',
        cameraError: 'Camera unavailable — enter the token manually.',
      } : prev));
      console.warn('QR camera start failed:', err);
    });

    return () => {
      isActive = false;
      // html5-qrcode throws synchronously (not a rejected promise) if stop()
      // is called on a scanner whose start() never resolved — e.g. camera
      // permission denied. That escapes this .catch() and, uncaught inside
      // an effect cleanup, was crashing the whole page (no error boundary
      // wraps it). Only call stop() once start() has actually succeeded.
      if (started) {
        html5Qr.stop().then(() => html5Qr.clear()).catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanModal?.step, scanModal?.mode, scanModal?.student?.id]);

  // ── Derived state ─────────────────────────────────────────────────────────
  const countdown = getCountdown();
  const parentStudents = students.filter(s => s.pickup_method === 'parent');
  const transportStudents = students.filter(s => s.pickup_method === 'transport');
  const counts = {
    total:      students.length,
    pending:    students.filter(s => s.arrival_status === 'pending').length,
    released:   students.filter(s => ['en_route_collector', 'en_route_bus'].includes(s.arrival_status)).length,
    home:       students.filter(s => s.arrival_status === 'safe_confirmed').length,
  };

  const collectorShareUrl = (token) =>
    `${window.location.origin}/security/collector-share?token=${token}`;

  return (
    <Layout>
    <div className="sec-page">
      {/* Header */}
      <div className="sec-header">
        <div className="sec-header-left">
          <div className="sec-shield-icon"><ShieldCheck size={20} /></div>
          <div>
            <p className="sec-header-title">Security Command Centre</p>
            <p className="sec-header-sub">{formatDate(now)}</p>
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div className="sec-clock">{formatTime(now)}</div>
          <div className="sec-clock-label">Local Time</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="sec-btn-sm" style={{ padding: '8px 16px', fontSize: 12 }}
            onClick={() => navigate('/security/admin-tracking')}>
            <Broadcast size={14} /> Live Map
          </button>
          <button className="sec-btn-sm" style={{ padding: '8px 16px', fontSize: 12 }}
            onClick={loadData}>
            <ArrowCounterClockwise size={14} /> Refresh
          </button>
        </div>
      </div>

      {/* Stats bar */}
      <div className="sec-stats-bar">
        <div className="sec-stat">
          <div className="sec-stat-value">{counts.total}</div>
          <div className="sec-stat-label">Total Students</div>
        </div>
        <div className="sec-stat">
          <div className="sec-stat-value amber">{counts.pending}</div>
          <div className="sec-stat-label">Awaiting Pickup</div>
        </div>
        <div className="sec-stat">
          <div className="sec-stat-value green">{counts.released}</div>
          <div className="sec-stat-label">En Route</div>
        </div>
        <div className="sec-stat">
          <div className="sec-stat-value green">{counts.home}</div>
          <div className="sec-stat-label">Home Safe</div>
        </div>
      </div>

      {/* Error / Loading */}
      {loadError && (
        <div style={{ margin: 16, padding: 14, background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 10, color: '#dc2626', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
          <WarningCircle size={16} /> {loadError}
          <button onClick={loadData} style={{ marginLeft: 12, textDecoration: 'underline', background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer' }}>Retry</button>
        </div>
      )}

      {/* Main layout */}
      <div className="sec-main">
        {/* LEFT */}
        <div className="sec-left">
          {/* Closing time scheduler */}
          <div className="sec-closing-card">
            <div className="sec-closing-header">
              <div>
                <p className="sec-closing-title"><HourglassMedium size={16} /> QR Dispatch</p>
                <p className="sec-closing-sub">Auto-send QR codes to parents at closing time</p>
              </div>
              <div className="sec-countdown">
                {[
                  { val: countdown.hh, label: 'hrs' },
                  { val: countdown.mm, label: 'min' },
                  { val: countdown.ss, label: 'sec' },
                ].map((unit, i) => (
                  <React.Fragment key={unit.label}>
                    {i > 0 && <span className="sec-countdown-sep">:</span>}
                    <div className="sec-countdown-unit">
                      <span className="sec-countdown-num">{String(unit.val).padStart(2, '0')}</span>
                      <div className="sec-countdown-label">{unit.label}</div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Closing Time</div>
                <input
                  type="time"
                  value={closingTime}
                  onChange={e => { setClosingTime(e.target.value); setQrsScheduled(false); setQrsSent(false); }}
                  className="sec-time-input"
                />
              </div>
              <div style={{ flex: 1 }}>
                {!qrsSent ? (
                  <>
                    <LockedFeature module="pickup_advanced">
                      <button
                        className={`sec-dispatch-btn ${qrsScheduled ? 'scheduled' : 'inactive'}`}
                        onClick={() => setQrsScheduled(true)}
                        disabled={qrsScheduled}
                      >
                        {qrsScheduled
                          ? <><CheckCircle size={14} /> Scheduled for {closingTime}</>
                          : 'Schedule Auto-Dispatch'}
                      </button>
                    </LockedFeature>
                    {!qrsScheduled && (
                      <LockedFeature module="pickup_advanced">
                        <button
                          className="sec-dispatch-btn scheduled"
                          style={{ marginTop: 6 }}
                          onClick={handleDispatchQRs}
                        >
                          Send QR Codes Now
                        </button>
                      </LockedFeature>
                    )}
                  </>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <button className="sec-dispatch-btn sent" disabled><CheckCircle size={14} weight="fill" /> QR Codes Sent</button>
                    {qrsDispatchedAt && (
                      <div style={{ fontSize: 12, color: '#6b7280' }}>
                        Dispatched at {formatTime(new Date(qrsDispatchedAt))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="sec-tabs">
            <button className={`sec-tab ${activeTab === 'students' ? 'active' : ''}`}
              onClick={() => setActiveTab('students')}>
              <UsersFour size={16} /> Parent Pickup ({parentStudents.length})
            </button>
            <button className={`sec-tab ${activeTab === 'transport' ? 'active' : ''}`}
              onClick={() => setActiveTab('transport')}>
              <Bus size={16} /> Transport ({transportStudents.length})
            </button>
            <LockedFeature module="student_safety">
              <button className={`sec-tab ${activeTab === 'queue' ? 'active' : ''}`}
                onClick={() => setActiveTab('queue')}>
                <Car size={16} /> Pickup Queue ({pickupQueue.on_the_way.length + pickupQueue.waiting.length})
              </button>
            </LockedFeature>
          </div>

          {loading && (
            <div style={{ padding: 40, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>
              Loading students...
            </div>
          )}

          {/* Parent pickup students */}
          {!loading && activeTab === 'students' && (
            <div className="sec-student-grid">
              {parentStudents.length === 0 && (
                <div style={{ color: '#6b7280', fontSize: 13, padding: 20 }}>
                  No students registered for parent pickup.
                </div>
              )}
              {parentStudents.map(student => {
                const status = student.arrival_status || 'pending';
                const meta = STATUS_META[status] || STATUS_META.pending;
                const released = status !== 'pending';
                return (
                  <div key={student.id} className={`sec-student-card ${status}`}>
                    <div className="sec-student-top">
                      <div className={`sec-avatar ${student.gender}`}>{initials(student.name)}</div>
                      <div style={{ flex: 1 }}>
                        <p className="sec-student-name">{student.name}</p>
                        <p className="sec-student-meta">{student.class}</p>
                      </div>
                      <span className={`sec-status-badge ${meta.badge}`} style={{ color: meta.color }}>
                        {meta.label}
                      </span>
                    </div>
                    {student.release_hold && (
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700,
                        color: '#dc2626', background: '#fee2e2', border: '1px solid #fca5a5',
                        borderRadius: 6, padding: '4px 8px', marginBottom: 6,
                      }}>
                        <LockKey size={13} weight="fill" /> HOLD: {student.release_hold_reason || 'Do not release'}
                      </div>
                    )}
                    {student.current_location && (
                      <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6 }}>
                        <Buildings size={12} /> Currently: {student.current_location}
                      </div>
                    )}
                    {student.ghana_post_address && (
                      <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6 }}>
                        <MapPin size={12} /> {student.ghana_post_address} · {student.neighbourhood}
                      </div>
                    )}
                    {student.parent_notes?.length > 0 && (
                      <div style={{
                        fontSize: 11, color: '#6b7280', fontStyle: 'italic',
                        borderLeft: '2px solid #16a34a', paddingLeft: 8, marginBottom: 8,
                      }}>
                        "{student.parent_notes[0].text.slice(0, 80)}{student.parent_notes[0].text.length > 80 ? '…' : ''}"
                      </div>
                    )}
                    <div className="sec-student-actions">
                      {!released ? (
                        <button className="sec-btn-sm primary" onClick={() => openScanner(student)}>
                          <Camera size={14} /> Scan QR
                        </button>
                      ) : (
                        <button className="sec-btn-sm" disabled style={{ opacity: 0.5 }}>
                          <CheckCircle size={14} /> Released
                        </button>
                      )}
                      <button className="sec-btn-sm"
                        onClick={() => navigate(`/security/admin-tracking`)}>
                        Track
                      </button>
                      {isAdmin && (
                        <LockedFeature module="student_safety">
                          <button className="sec-btn-sm" onClick={() => openEditPickup(student)}>
                            <PencilSimple size={14} /> Pickup
                          </button>
                        </LockedFeature>
                      )}
                      <LockedFeature module="student_safety">
                        <button className="sec-btn-sm" onClick={() => openLocationDialog(student)}>
                          <Buildings size={14} /> Location
                        </button>
                      </LockedFeature>
                      {student.release_hold ? (
                        <LockedFeature module="student_safety">
                          <button className="sec-btn-sm" onClick={() => handleClearHold(student)} disabled={savingHold}
                            style={{ color: '#16a34a', borderColor: '#86efac' }}>
                            <LockKeyOpen size={14} /> Lift Hold
                          </button>
                        </LockedFeature>
                      ) : (
                        <LockedFeature module="student_safety">
                          <button className="sec-btn-sm" onClick={() => openHoldDialog(student)}
                            style={{ color: '#dc2626', borderColor: '#fca5a5' }}>
                            <LockKey size={14} /> Hold
                          </button>
                        </LockedFeature>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Transport routes */}
          {!loading && activeTab === 'transport' && (
            <div>
              {routes.length === 0 && (
                <div style={{ color: '#6b7280', fontSize: 13, padding: 20 }}>
                  No transport routes found.
                </div>
              )}
              {routes.map(route => {
                const routeStudents = transportStudents.filter(
                  s => s.transport_route_id === route.id
                );
                const dispatched = routeStudents.some(
                  s => s.arrival_status === 'en_route_bus'
                );
                return (
                  <div key={route.id} className="sec-transport-card">
                    <div className="sec-transport-top">
                      <div className="sec-bus-icon"><Bus size={20} /></div>
                      <div style={{ flex: 1 }}>
                        <p className="sec-transport-name">{route.name}</p>
                        <p className="sec-transport-meta">
                          {route.vehicle_plate} · {route.driver_name} · {routeStudents.length} students
                        </p>
                      </div>
                      <span className={`sec-status-badge ${dispatched ? 'badge-verified' : 'badge-pending'}`}>
                        {dispatched ? <><Broadcast size={12} weight="fill" /> Live</> : 'Pending'}
                      </span>
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      {routeStudents.map(s => (
                        <div key={s.id} style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          padding: '4px 0', fontSize: 12, color: '#6b7280',
                          borderBottom: '1px solid #f3f4f6',
                        }}>
                          <div className={`sec-avatar ${s.gender}`} style={{ width: 26, height: 26, fontSize: 10 }}>
                            {initials(s.name)}
                          </div>
                          <span style={{ color: '#111827', flex: 1 }}>{s.name}</span>
                          <span style={{ fontSize: 10, color: STATUS_META[s.arrival_status]?.color }}>
                            {STATUS_META[s.arrival_status]?.label}
                          </span>
                          {isAdmin && (
                            <button
                              onClick={() => openEditPickup(s)}
                              style={{
                                fontSize: 10, padding: '2px 6px', borderRadius: 6,
                                border: '1px solid #e5e7eb', background: 'white',
                                color: '#6b7280', cursor: 'pointer',
                              }}
                            >
                              <PencilSimple size={11} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="sec-btn-sm"
                        onClick={() => setQrModal({ route })}>
                        Generate Driver QR
                      </button>
                      {!dispatched ? (
                        <button className="sec-btn-sm primary"
                          onClick={() => handleDispatchTransport(route)}>
                          Dispatch Route
                        </button>
                      ) : (
                        <button className="sec-btn-sm primary"
                          onClick={() => navigate(`/security/driver?route=${route.id}`)}>
                          <MapPin size={14} /> Live Tracking
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pickup Queue — drive-through staging: who's on the way, who's arrived and waiting */}
          {activeTab === 'queue' && (
            <div>
              {loadingQueue && (
                <div style={{ padding: 20, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>
                  Loading queue...
                </div>
              )}
              {!loadingQueue && (
                <>
                  <p className="sec-section-title">On The Way ({pickupQueue.on_the_way.length})</p>
                  {pickupQueue.on_the_way.length === 0 && (
                    <div style={{ color: '#6b7280', fontSize: 13, padding: '4px 0 16px' }}>
                      No parents have signalled they're on their way yet.
                    </div>
                  )}
                  <div className="sec-student-grid" style={{ marginBottom: 20 }}>
                    {pickupQueue.on_the_way.map(entry => (
                      <div key={entry.student_id} className="sec-student-card pending">
                        <div className="sec-student-top">
                          <div className="sec-avatar">{initials(entry.student_name)}</div>
                          <div style={{ flex: 1 }}>
                            <p className="sec-student-name">{entry.student_name}</p>
                            <p className="sec-student-meta">
                              {entry.eta_minutes ? `ETA ~${entry.eta_minutes} min` : 'On the way'}
                            </p>
                          </div>
                          <span className="sec-status-badge badge-pending" style={{ color: '#d97706' }}>
                            <Car size={12} weight="fill" /> Coming
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <p className="sec-section-title">Waiting to Be Dismissed ({pickupQueue.waiting.length})</p>
                  {pickupQueue.waiting.length === 0 && (
                    <div style={{ color: '#6b7280', fontSize: 13, padding: '4px 0' }}>
                      No one is currently waiting in the pickup queue.
                    </div>
                  )}
                  <div className="sec-student-grid">
                    {pickupQueue.waiting.map(entry => (
                      <div key={entry.student_id} className="sec-student-card verified">
                        <div className="sec-student-top">
                          <div className="sec-avatar">{initials(entry.student_name)}</div>
                          <div style={{ flex: 1 }}>
                            <p className="sec-student-name">{entry.student_name}</p>
                            <p className="sec-student-meta">
                              {entry.arrived_at ? `Arrived ${formatTime(new Date(entry.arrived_at))}` : 'Arrived'}
                            </p>
                          </div>
                          <span className="sec-status-badge badge-verified" style={{ color: '#16a34a', fontWeight: 700 }}>
                            #{entry.queue_position}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* RIGHT: notifications */}
        <div className="sec-right">
          <p className="sec-section-title">Activity Feed</p>
          {notifications.length === 0 && (
            <div style={{ fontSize: 12, color: '#9ca3af', padding: '20px 0', textAlign: 'center' }}>
              No activity yet today.
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {notifications.map(n => (
              <div key={n.id} className="sec-notification">
                <div className={`sec-notification-dot ${n.color}`} />
                <span style={{ flex: 1, color: '#374151', fontSize: 12 }}>{n.text}</span>
                <span className="sec-notification-time">{n.time}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Scanner Modal ─────────────────────────────────────────────────── */}
      {scanModal && (
        <div className="sec-modal-overlay" onClick={() => setScanModal(null)}>
          <div className="sec-modal" onClick={e => e.stopPropagation()}>

            {/* Step: enter token */}
            {(scanModal.step === 'enter_token' || scanModal.step === 'verifying') && (
              <>
                <p className="sec-modal-title">Scan QR for {scanModal.student.name}</p>
                <div style={{
                  background: '#f0fdf4', border: '1px solid #bbf7d0',
                  borderRadius: 12, padding: 16, marginBottom: 20,
                  display: 'flex', alignItems: 'center', gap: 12,
                }}>
                  <div className={`sec-avatar ${scanModal.student.gender}`} style={{ width: 44, height: 44, flexShrink: 0 }}>
                    {initials(scanModal.student.name)}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, color: '#111827' }}>{scanModal.student.name}</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>{scanModal.student.class}</div>
                    {scanModal.student.ghana_post_address && (
                      <div style={{ fontSize: 11, color: '#16a34a', fontFamily: 'monospace', marginTop: 2 }}>
                        {scanModal.student.ghana_post_address}
                      </div>
                    )}
                  </div>
                </div>

                {scanModal.mode === 'camera' ? (
                  <div style={{ marginBottom: 16 }}>
                    <div
                      id={QR_READER_ELEMENT_ID}
                      style={{
                        width: '100%', minHeight: 240, borderRadius: 12,
                        overflow: 'hidden', background: '#0f172a',
                      }}
                    />
                    <p style={{ fontSize: 11, color: '#6b7280', textAlign: 'center', margin: '8px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                      {scanModal.step === 'verifying'
                        ? <><HourglassMedium size={12} /> Verifying scanned code...</>
                        : 'Point the camera at the collector\'s QR code'}
                    </p>
                    <button
                      className="sec-modal-btn secondary"
                      style={{ marginTop: 10, width: '100%' }}
                      onClick={() => setScanModal(prev => ({ ...prev, mode: 'manual', cameraError: null }))}
                      disabled={scanModal.step === 'verifying'}
                    >
                      <Keyboard size={14} /> Type the token instead
                    </button>
                  </div>
                ) : (
                  <div style={{ marginBottom: 16 }}>
                    {scanModal.cameraError && (
                      <div style={{ fontSize: 12, color: '#dc2626', marginBottom: 8 }}>
                        {scanModal.cameraError}
                      </div>
                    )}
                    <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 6 }}>
                      Enter or paste the QR token from the collector's phone:
                    </label>
                    <input
                      type="text"
                      value={scanModal.tokenInput}
                      onChange={e => setScanModal(prev => ({ ...prev, tokenInput: e.target.value }))}
                      onKeyDown={e => e.key === 'Enter' && handleVerifyToken()}
                      placeholder="e.g. 3f8a2b1c-4d5e-6789-..."
                      autoFocus
                      style={{
                        width: '100%', boxSizing: 'border-box',
                        padding: '10px 12px', borderRadius: 8,
                        border: '1px solid #d1fae5', fontSize: 13,
                        fontFamily: 'monospace', outline: 'none',
                        background: '#f8fafc', color: '#111827',
                      }}
                    />
                    <button
                      className="sec-modal-btn secondary"
                      style={{ marginTop: 10, width: '100%' }}
                      onClick={() => setScanModal(prev => ({ ...prev, mode: 'camera', cameraError: null }))}
                    >
                      <Camera size={14} /> Use camera instead
                    </button>
                  </div>
                )}

                <div className="sec-modal-actions">
                  {scanModal.mode === 'manual' && (
                    <button
                      className="sec-modal-btn primary"
                      onClick={() => handleVerifyToken()}
                      disabled={!scanModal.tokenInput?.trim() || scanModal.step === 'verifying'}
                    >
                      {scanModal.step === 'verifying'
                        ? <><HourglassMedium size={14} /> Verifying...</>
                        : <><CheckCircle size={14} /> Verify Token</>}
                    </button>
                  )}
                  <button className="sec-modal-btn secondary" onClick={() => setScanModal(null)}>
                    Cancel
                  </button>
                </div>
              </>
            )}

            {/* Step: result */}
            {scanModal.step === 'result' && scanModal.result && (() => {
              const authorized = scanModal.result.result === 'authorized';
              const collectorToken = scanModal.result.collector_session_token;
              return (
                <>
                  <p className="sec-modal-title">Verification Result</p>
                  <div className={`sec-verify-result ${authorized ? 'success' : 'failure'}`}>
                    <div className="sec-verify-icon">{authorized ? <CheckCircle size={32} weight="fill" /> : <Prohibit size={32} weight="fill" />}</div>
                    <p className="sec-verify-status">{authorized ? 'AUTHORIZED' : 'UNAUTHORIZED'}</p>
                    <p style={{ fontSize: 12, color: '#6b7280', margin: '4px 0 0' }}>
                      {authorized
                        ? 'QR code verified — student may be released'
                        : scanModal.result.reason || 'Token is invalid, expired, or already used'}
                    </p>
                  </div>

                  {authorized && collectorToken && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#111827', marginBottom: 8 }}>
                        Ask the collector to scan this with their phone:
                      </div>
                      <div style={{
                        background: 'white', border: '1px solid #e5e7eb',
                        borderRadius: 12, padding: 16, textAlign: 'center',
                      }}>
                        <QRCode
                          value={collectorShareUrl(collectorToken)}
                          size={160}
                          level="H"
                        />
                        <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 8 }}>
                          Opens live location sharing on collector's phone
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="sec-modal-actions">
                    {authorized ? (
                      <button className="sec-modal-btn primary" onClick={() => setScanModal(null)}>
                        <CheckCircle size={14} /> Done
                      </button>
                    ) : (
                      <button className="sec-modal-btn danger"><Siren size={14} /> Alert Security</button>
                    )}
                    <button className="sec-modal-btn secondary" onClick={() => setScanModal(null)}>
                      Close
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* ── Driver QR Modal ───────────────────────────────────────────────── */}
      {qrModal && (
        <div className="sec-modal-overlay" onClick={() => setQrModal(null)}>
          <div className="sec-modal sec-qr-modal" onClick={e => e.stopPropagation()}>
            <p className="sec-modal-title">Driver QR — {qrModal.route.name}</p>
            <p style={{ fontSize: 12, color: '#6b7280', margin: '-12px 0 16px' }}>
              Scan with the driver's phone camera to open their live tracking page directly —
              they'll still need to be logged in as the driver assigned to this route to start sharing location.
            </p>
            <div className="sec-qr-wrap">
              <QRCode
                value={`${window.location.origin}/security/driver?route=${qrModal.route.id}`}
                size={200}
                level="H"
              />
            </div>
            <p className="sec-qr-caption">
              {qrModal.route.vehicle_plate} · {qrModal.route.driver_name}
            </p>
            <div className="sec-modal-actions" style={{ marginTop: 16 }}>
              <button className="sec-modal-btn primary"
                onClick={() => { setQrModal(null); navigate(`/security/driver?route=${qrModal.route.id}`); }}>
                View Driver Page
              </button>
              <button className="sec-modal-btn secondary" onClick={() => setQrModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Pickup Method Modal (admin only) ───────────────────────────── */}
      <DialogModal
        isOpen={Boolean(holdDialog)}
        onClose={() => setHoldDialog(null)}
        title="Place Release Hold"
        message={`${holdDialog?.studentName || 'This student'} will be blocked from leaving campus — both QR-scan and staff-driven pickup are hard-denied until this hold is lifted.`}
        type="error"
        input={{ label: 'Reason', placeholder: 'Why is this student being held?', required: true }}
        primaryAction={{ label: savingHold ? 'Saving...' : 'Place Hold', onClick: handleSetHold }}
        secondaryAction={{ label: 'Cancel' }}
        showCloseButton={!savingHold}
        isLoading={savingHold}
        disableCloseWhenLoading={true}
      />

      <DialogModal
        isOpen={Boolean(locationDialog)}
        onClose={() => setLocationDialog(null)}
        title="Update Location"
        message={`Where is ${locationDialog?.studentName || 'this student'} right now?`}
        type="info"
        input={{ label: 'Location', placeholder: 'e.g. Classroom 3B, Library, Nurse\'s Office', defaultValue: locationDialog?.location || '', required: true }}
        primaryAction={{ label: savingLocation ? 'Saving...' : 'Update', onClick: handleSetLocation }}
        secondaryAction={{ label: 'Cancel' }}
        showCloseButton={!savingLocation}
        isLoading={savingLocation}
        disableCloseWhenLoading={true}
      />

      {editModal && (
        <div className="sec-modal-overlay" onClick={() => setEditModal(null)}>
          <div className="sec-modal" onClick={e => e.stopPropagation()}>
            <p className="sec-modal-title">Pickup Method — {editModal.student.name}</p>

            <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
              <button
                onClick={() => setEditModal(prev => ({ ...prev, pickupMethod: 'parent', error: null }))}
                style={{
                  flex: 1, padding: '12px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  border: editModal.pickupMethod === 'parent' ? '2px solid #16a34a' : '1px solid #e5e7eb',
                  background: editModal.pickupMethod === 'parent' ? '#f0fdf4' : 'white',
                  color: editModal.pickupMethod === 'parent' ? '#16a34a' : '#6b7280',
                }}
              >
                <UsersFour size={16} /> Parent Pickup
              </button>
              <button
                onClick={() => setEditModal(prev => ({ ...prev, pickupMethod: 'transport', error: null }))}
                style={{
                  flex: 1, padding: '12px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  border: editModal.pickupMethod === 'transport' ? '2px solid #16a34a' : '1px solid #e5e7eb',
                  background: editModal.pickupMethod === 'transport' ? '#f0fdf4' : 'white',
                  color: editModal.pickupMethod === 'transport' ? '#16a34a' : '#6b7280',
                }}
              >
                <Bus size={16} /> School Transport
              </button>
            </div>

            {editModal.pickupMethod === 'transport' && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 6 }}>
                  Assign to route:
                </label>
                {routes.length === 0 ? (
                  <div style={{ fontSize: 12, color: '#dc2626' }}>
                    No transport routes exist for this school yet.
                  </div>
                ) : (
                  <select
                    value={editModal.transportRouteId}
                    onChange={e => setEditModal(prev => ({ ...prev, transportRouteId: e.target.value }))}
                    style={{
                      width: '100%', boxSizing: 'border-box', padding: '10px 12px',
                      borderRadius: 8, border: '1px solid #d1fae5', fontSize: 13,
                      outline: 'none', background: '#f8fafc', color: '#111827',
                    }}
                  >
                    {routes.map(r => (
                      <option key={r.id} value={r.id}>{r.route_name}</option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {editModal.error && (
              <div style={{ fontSize: 12, color: '#dc2626', marginBottom: 12 }}>
                {editModal.error}
              </div>
            )}

            <div className="sec-modal-actions">
              <button
                className="sec-modal-btn primary"
                onClick={handleSavePickup}
                disabled={editModal.saving}
              >
                {editModal.saving
                  ? <><HourglassMedium size={14} /> Saving...</>
                  : <><CheckCircle size={14} /> Save</>}
              </button>
              <button className="sec-modal-btn secondary" onClick={() => setEditModal(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </Layout>
  );
}
